import {
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Cancel, CheckCircle, Forum, ReceiptLong, TaskAlt } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";

import colors from "assets/theme/base/colors";
import rgba from "assets/theme/functions/rgba";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import FormField from "layouts/applications/wizard/components/FormField";
import ApiCalls from "services/flick_erp_api_call";

const statusColor = {
  pending: "warning",
  needs_info: "info",
  approved: "success",
  rejected: "error",
  cancelled: "default",
};

const statusLabel = {
  pending: "Pending",
  needs_info: "Needs Info",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
};

const blankRequest = {
  request_type: "reimbursement",
  line_type: "allowance",
  employee: "",
  selected_approver: "",
  hr_approver: "",
  title: "",
  description: "",
  requested_amount: "",
  start_date: "",
  end_date: "",
  bill: null,
};

const employeeName = (employee) => {
  if (!employee) return "";
  const user = employee.user || {};
  return user.full_name || `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email || employee.name || `Employee #${employee.id}`;
};

export function Expenses({ permissions = [], modulePermissions = {} }) {
  const theme = useTheme();
  const palette = theme.palette;
  const darkMode = false;
  const loggedUser = JSON.parse(localStorage.getItem("user"));

  const [requests, setRequests] = useState({ columns: [], rows: [] });
  const [employees, setEmployees] = useState([]);
  const [createDialog, setCreateDialog] = useState(false);
  const [threadDialog, setThreadDialog] = useState(false);
  const [activeRequest, setActiveRequest] = useState(null);
  const [requestPayload, setRequestPayload] = useState(blankRequest);
  const [commentPayload, setCommentPayload] = useState({ message: "", attachment: null });
  const [decisionNote, setDecisionNote] = useState("");
  const [finalPayload, setFinalPayload] = useState({ approved_amount: "", note: "" });

  const approvePermission = "payrole | expense or incentive or deduction|approve";
  const requestApprovePermission = "payrole | payroll extra request|approve";
  const updatePermission = modulePermissions?.update_permission || "payrole | expense or incentive or deduction|update";
  const canCreate = true;
  const canApprove =
    permissions?.includes(updatePermission) ||
    permissions?.includes(approvePermission) ||
    permissions?.includes(requestApprovePermission);

  const dialogPaperSx = {
    backgroundColor: darkMode ? palette.background.card : colors.white.main,
    boxShadow: darkMode ? "0 24px 48px rgba(2, 6, 23, 0.42)" : "none",
    width: "auto",
    maxWidth: "none",
    borderRadius: "20px",
    border: `1px solid ${darkMode ? rgba(palette.white.main, 0.08) : rgba(colors.secondary.main, 0.14)}`,
  };

  const buildRequestFormData = () => {
    const formData = new FormData();
    ["request_type", "line_type", "title", "description", "requested_amount", "start_date", "end_date"].forEach((field) => {
      formData.append(field, requestPayload[field] || "");
    });
    if (canApprove && requestPayload.employee) formData.append("employee", requestPayload.employee);
    if (requestPayload.selected_approver) formData.append("selected_approver", requestPayload.selected_approver);
    if (requestPayload.hr_approver) formData.append("hr_approver", requestPayload.hr_approver);
    if (requestPayload.bill instanceof File) formData.append("bill", requestPayload.bill);
    return formData;
  };

  async function getEmployees() {
    const res = await ApiCalls.getEmployeesList("?status=active");
    if (res.success && res.code === 200) setEmployees(res.data || []);
  }

  async function getRequests() {
    const res = await ApiCalls.getPayrollExtraRequests();
    if (res.success && res.code === 200) {
      setRequests((state) => ({ ...state, rows: res.data || [] }));
      if (activeRequest) {
        const refreshed = (res.data || []).find((item) => item.id === activeRequest.id);
        if (refreshed) setActiveRequest(refreshed);
      }
    }
  }

  async function submitRequest() {
    const res = await ApiCalls.createPayrollExtraRequest(buildRequestFormData(), true);
    if (res.success) {
      setCreateDialog(false);
      setRequestPayload(blankRequest);
      getRequests();
    }
  }

  async function addComment() {
    if (!activeRequest || !commentPayload.message) return;
    const formData = new FormData();
    formData.append("message", commentPayload.message);
    if (commentPayload.attachment instanceof File) formData.append("attachment", commentPayload.attachment);
    const res = await ApiCalls.commentPayrollExtraRequest(activeRequest.id, formData, true);
    if (res.success) {
      setActiveRequest(res.data);
      setCommentPayload({ message: "", attachment: null });
      getRequests();
    }
  }

  async function approveStep(row = activeRequest) {
    if (!row) return;
    const res = await ApiCalls.approvePayrollExtraRequestStep(row.id, { note: decisionNote });
    if (res.success) {
      setDecisionNote("");
      setActiveRequest(res.data);
      getRequests();
    }
  }

  async function rejectRequest(row = activeRequest) {
    if (!row) return;
    const res = await ApiCalls.rejectPayrollExtraRequest(row.id, { note: decisionNote });
    if (res.success) {
      setDecisionNote("");
      setActiveRequest(res.data);
      getRequests();
    }
  }

  async function needsInfo(row = activeRequest) {
    if (!row) return;
    const res = await ApiCalls.needsInfoPayrollExtraRequest(row.id);
    if (res.success) {
      setActiveRequest(res.data);
      getRequests();
    }
  }

  async function finalizeRequest() {
    if (!activeRequest || !finalPayload.approved_amount) return;
    const res = await ApiCalls.finalizePayrollExtraRequest(activeRequest.id, finalPayload);
    if (res.success) {
      setFinalPayload({ approved_amount: "", note: "" });
      setActiveRequest(res.data);
      getRequests();
    }
  }

  async function cancelRequest(row) {
    const res = await ApiCalls.cancelPayrollExtraRequest(row.id);
    if (res.success) getRequests();
  }

  const hasPendingApproval = (row) =>
    !row?.final_payroll_extra &&
    (row.approvals || []).some((approval) => approval.approver === loggedUser?.employee_id && approval.status === "pending");

  const columns = useMemo(
    () => [
      { Header: "EMPLOYEE", accessor: "employee_name" },
      { Header: "REQUEST", accessor: "title" },
      { Header: "TYPE", accessor: "request_type" },
      { Header: "AMOUNT", accessor: "requested_amount" },
      {
        Header: "STATUS",
        accessor: "status",
        Cell: ({ row }) => (
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" color={statusColor[row.original.status] || "default"} label={statusLabel[row.original.status] || row.original.status} />
            {row.original.final_payroll_extra && <Chip size="small" color="success" variant="outlined" label="Payroll added" />}
          </Stack>
        ),
      },
      { Header: "REQUESTED BY", accessor: "requested_by_name" },
      {
        Header: "BILL",
        accessor: "bill",
        Cell: ({ row }) =>
          row.original.bill ? (
            <Tooltip title="Open bill">
              <IconButton size="small" component="a" href={row.original.bill} target="_blank" rel="noreferrer">
                <ReceiptLong fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <MDTypography variant="caption" color="text">-</MDTypography>
          ),
      },
      {
        Header: "ACTION",
        accessor: "action",
        Cell: ({ row }) => {
          const isOwn = row.original.employee === loggedUser?.employee_id || row.original.requested_by === loggedUser?.employee_id;
          const isAddedToPayroll = Boolean(row.original.final_payroll_extra);
          const pendingApproval = hasPendingApproval(row.original);
          const canFinalize = canApprove && ["pending", "needs_info"].includes(row.original.status) && !isAddedToPayroll;
          const canCancel = isOwn && ["pending", "needs_info"].includes(row.original.status) && !isAddedToPayroll;
          return (
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Open thread">
                <IconButton
                  size="small"
                  onClick={() => {
                    setActiveRequest(row.original);
                    setFinalPayload({ approved_amount: row.original.approved_amount || row.original.requested_amount || "", note: "" });
                    setThreadDialog(true);
                  }}
                >
                  <Forum fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={pendingApproval ? "Approve your step" : "No pending approval assigned to you"}>
                <span>
                  <IconButton size="small" color="success" disabled={!pendingApproval} onClick={() => approveStep(row.original)}>
                    <CheckCircle fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={canFinalize ? "HR final approval" : "Only HR can finalize pending requests"}>
                <span>
                  <IconButton
                    size="small"
                    color="info"
                    disabled={!canFinalize}
                    onClick={() => {
                      setActiveRequest(row.original);
                      setFinalPayload({ approved_amount: row.original.approved_amount || row.original.requested_amount || "", note: "" });
                      setThreadDialog(true);
                    }}
                  >
                    <TaskAlt fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={canCancel ? "Cancel request" : "Only your pending request can be cancelled"}>
                <span>
                  <IconButton size="small" disabled={!canCancel} onClick={() => cancelRequest(row.original)}>
                    <Cancel fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [canApprove, loggedUser?.employee_id]
  );

  useEffect(() => {
    getRequests();
    getEmployees();
  }, []);

  useEffect(() => {
    setRequests((state) => ({ ...state, columns }));
  }, [columns]);

  return (
    <>
      <Card>
        <MDBox pt={3} pl={3} pb={4} pr={3} display="flex" justifyContent="space-between" alignItems="center">
          <MDBox>
            <MDTypography variant="h4">Expense Requests</MDTypography>
            <MDTypography variant="button" color="text">
              Request reimbursement, discuss in one thread, collect manager/approver input, and let HR add the final amount to payroll.
            </MDTypography>
          </MDBox>
          {canCreate && (
            <Fab color="info" onClick={() => setCreateDialog(true)}>
              <MDTypography color="white">+</MDTypography>
            </Fab>
          )}
        </MDBox>
        <MDBox>
          <DataTable table={requests} entriesPerPage={{ defaultValue: 100, entries: [5, 10, 15, 20, 25, 30] }} showTotalEntries isSorted={false} />
        </MDBox>
      </Card>

      <Dialog PaperProps={{ sx: dialogPaperSx }} open={createDialog} onClose={() => setCreateDialog(false)}>
        <DialogTitle sx={{ width: "100%", p: "20px", background: `linear-gradient(135deg, ${colors.info.main} 0%, ${colors.info.focus} 100%)`, color: "white" }}>
          <Typography sx={{ color: "white", fontWeight: "Medium" }}>Create Expense Request</Typography>
        </DialogTitle>
        <DialogContent sx={{ p: "4px", backgroundColor: colors.white.main }}>
          <MDBox p={3} lineHeight={1}>
            <Grid container spacing={2}>
              {canApprove && (
                <Grid item xs={12} sm={6}>
                  <FormField
                    select
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    label="Employee"
                    value={requestPayload.employee}
                    onChange={(e) => setRequestPayload((state) => ({ ...state, employee: e.target.value }))}
                  >
                    <option value="">Self</option>
                    {employees.map((employee) => <option key={employee.id} value={employee.id}>{employeeName(employee)}</option>)}
                  </FormField>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <FormField
                  select
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                  label="Request Type"
                  value={requestPayload.request_type}
                  onChange={(e) => setRequestPayload((state) => ({ ...state, request_type: e.target.value }))}
                >
                  <option value="reimbursement">Reimbursement</option>
                  <option value="overtime">Overtime</option>
                  <option value="extra_working_day">Extra Working Day</option>
                  <option value="travel">Travel</option>
                  <option value="food">Food</option>
                  <option value="other">Other</option>
                </FormField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField
                  select
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                  label="Payroll Line Type"
                  value={requestPayload.line_type}
                  onChange={(e) => setRequestPayload((state) => ({ ...state, line_type: e.target.value }))}
                >
                  <option value="allowance">Allowance</option>
                  <option value="deduction">Deduction</option>
                </FormField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField label="Title" value={requestPayload.title} onChange={(e) => setRequestPayload((state) => ({ ...state, title: e.target.value }))} placeholder="Client approved extra working day" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField label="Requested Amount" value={requestPayload.requested_amount} inputProps={{ type: "number" }} onChange={(e) => setRequestPayload((state) => ({ ...state, requested_amount: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField
                  select
                  SelectProps={{ native: true }}
                  InputLabelProps={{ shrink: true }}
                  label="Selected Approver"
                  value={requestPayload.selected_approver}
                  onChange={(e) => setRequestPayload((state) => ({ ...state, selected_approver: e.target.value }))}
                >
                  <option value="">No extra approver</option>
                  {employees.map((employee) => <option key={employee.id} value={employee.id}>{employeeName(employee)}</option>)}
                </FormField>
              </Grid>
              {canApprove && (
                <Grid item xs={12} sm={6}>
                  <FormField
                    select
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    label="HR Approver"
                    value={requestPayload.hr_approver}
                    onChange={(e) => setRequestPayload((state) => ({ ...state, hr_approver: e.target.value }))}
                  >
                    <option value="">Any HR approver</option>
                    {employees.map((employee) => <option key={employee.id} value={employee.id}>{employeeName(employee)}</option>)}
                  </FormField>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <MDButton variant="outlined" color="info" component="label" fullWidth sx={{ height: "100%" }}>
                  {requestPayload.bill instanceof File ? requestPayload.bill.name : "Upload Bill / Proof"}
                  <input hidden type="file" accept="image/*,.pdf" onChange={(e) => setRequestPayload((state) => ({ ...state, bill: e.target.files?.[0] || null }))} />
                </MDButton>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField label="Start Date" InputLabelProps={{ shrink: true }} value={requestPayload.start_date} inputProps={{ type: "date" }} onChange={(e) => setRequestPayload((state) => ({ ...state, start_date: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormField label="End Date" InputLabelProps={{ shrink: true }} value={requestPayload.end_date} inputProps={{ type: "date" }} onChange={(e) => setRequestPayload((state) => ({ ...state, end_date: e.target.value }))} />
              </Grid>
              <Grid item xs={12}>
                <FormField label="Details" multiline rows={4} value={requestPayload.description} onChange={(e) => setRequestPayload((state) => ({ ...state, description: e.target.value }))} />
              </Grid>
            </Grid>
            <MDBox pt={3} display="flex" justifyContent="flex-end">
              <MDButton variant="gradient" color="dark" onClick={submitRequest}>Submit Request</MDButton>
            </MDBox>
          </MDBox>
        </DialogContent>
      </Dialog>

      <Dialog PaperProps={{ sx: dialogPaperSx }} open={threadDialog} onClose={() => setThreadDialog(false)}>
        <DialogTitle sx={{ p: "20px" }}>
          <Typography fontWeight="Medium">{activeRequest?.title || "Expense Thread"}</Typography>
        </DialogTitle>
        <DialogContent>
          <MDBox p={2} width={{ xs: "300px", sm: "680px" }}>
            <Stack spacing={1.25}>
              <MDTypography variant="button" color="text">{activeRequest?.description}</MDTypography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip size="small" color={statusColor[activeRequest?.status] || "default"} label={statusLabel[activeRequest?.status] || activeRequest?.status} />
                <Chip size="small" variant="outlined" label={`Requested: ${activeRequest?.requested_amount || "-"}`} />
                {activeRequest?.approved_amount && <Chip size="small" color="success" variant="outlined" label={`Approved: ${activeRequest.approved_amount}`} />}
              </Stack>
              {(activeRequest?.approvals || []).map((approval) => (
                <MDBox key={approval.id} p={1.25} borderRadius="lg" sx={{ backgroundColor: rgba(colors.info.main, 0.08) }}>
                  <MDTypography variant="caption" fontWeight="medium">
                    {approval.role_display || approval.role}: {approval.approver_name} - {approval.status_display || approval.status}
                  </MDTypography>
                  {approval.note && <MDTypography variant="caption" color="text" display="block">{approval.note}</MDTypography>}
                </MDBox>
              ))}
              {(activeRequest?.comments || []).map((comment) => (
                <MDBox key={comment.id} p={1.25} borderRadius="lg" sx={{ backgroundColor: rgba(colors.secondary.main, 0.08) }}>
                  <MDTypography variant="caption" fontWeight="medium">{comment.employee_name}</MDTypography>
                  <MDTypography variant="button" color="text" display="block">{comment.message}</MDTypography>
                  {comment.attachment && (
                    <MDButton size="small" variant="text" color="info" component="a" href={comment.attachment} target="_blank" rel="noreferrer">Open attachment</MDButton>
                  )}
                </MDBox>
              ))}
              <FormField label="Reply in thread" multiline rows={3} value={commentPayload.message} onChange={(e) => setCommentPayload((state) => ({ ...state, message: e.target.value }))} />
              <Stack direction="row" spacing={1}>
                <MDButton variant="outlined" color="info" component="label">
                  {commentPayload.attachment instanceof File ? commentPayload.attachment.name : "Attach"}
                  <input hidden type="file" accept="image/*,.pdf" onChange={(e) => setCommentPayload((state) => ({ ...state, attachment: e.target.files?.[0] || null }))} />
                </MDButton>
                <MDButton variant="gradient" color="info" onClick={addComment}>Send Reply</MDButton>
              </Stack>
              <FormField label="Approval / rejection note" value={decisionNote} onChange={(e) => setDecisionNote(e.target.value)} />
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <MDButton color="success" variant="outlined" disabled={!activeRequest || activeRequest.final_payroll_extra || !hasPendingApproval(activeRequest)} onClick={() => approveStep()}>Approve My Step</MDButton>
                <MDButton color="info" variant="outlined" disabled={!activeRequest || activeRequest.final_payroll_extra || (!canApprove && !hasPendingApproval(activeRequest))} onClick={() => needsInfo()}>Needs Info</MDButton>
                <MDButton color="error" variant="outlined" disabled={!activeRequest || activeRequest.final_payroll_extra || (!canApprove && !hasPendingApproval(activeRequest))} onClick={() => rejectRequest()}>Reject</MDButton>
              </Stack>
              {canApprove && activeRequest && !activeRequest.final_payroll_extra && ["pending", "needs_info"].includes(activeRequest.status) && (
                <MDBox mt={2} p={2} borderRadius="lg" sx={{ backgroundColor: rgba(colors.success.main, 0.08) }}>
                  <Stack spacing={1.5}>
                    <MDTypography variant="button" fontWeight="medium">
                      HR final approval creates the payroll extra line.
                    </MDTypography>
                    <FormField label="Final Approved Amount" value={finalPayload.approved_amount} inputProps={{ type: "number" }} onChange={(e) => setFinalPayload((state) => ({ ...state, approved_amount: e.target.value }))} />
                    <FormField label="Final HR Note" value={finalPayload.note} onChange={(e) => setFinalPayload((state) => ({ ...state, note: e.target.value }))} />
                    <MDButton color="success" variant="gradient" onClick={finalizeRequest}>Finalize And Add To Payroll</MDButton>
                  </Stack>
                </MDBox>
              )}
              {activeRequest?.final_payroll_extra && (
                <MDBox p={1.25} borderRadius="lg" sx={{ backgroundColor: rgba(colors.success.main, 0.08) }}>
                  <MDTypography variant="button" color="success">
                    This request is finalized and added to payroll.
                  </MDTypography>
                </MDBox>
              )}
            </Stack>
          </MDBox>
        </DialogContent>
      </Dialog>
    </>
  );
}
